import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LoadingScreen from "./LoadingScreen";

const ACCESS_CODE = "Confiance";
const STORAGE_KEY = "jd_access_granted";
const LOADING_SHOWN_KEY = "jd_loading_shown";

export default function AccessGate({ children }) {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);

  useEffect(() => {
    // Check if already authorized
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const loadingShown = sessionStorage.getItem(LOADING_SHOWN_KEY);
    
    if (stored === "true") {
      setIsAuthorized(true);
      // If already authorized and loading was shown, skip loading
      if (loadingShown === "true") {
        setLoadingComplete(true);
      } else {
        // Show loading for returning authorized users who haven't seen it
        setShowLoading(true);
      }
    }
    
    setIsCheckingAuth(false);
  }, []);

  const handleLoadingComplete = () => {
    setShowLoading(false);
    setLoadingComplete(true);
    sessionStorage.setItem(LOADING_SHOWN_KEY, "true");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code === ACCESS_CODE) {
      sessionStorage.setItem(STORAGE_KEY, "true");
      setIsAuthorized(true);
      setError(false);
      // Show loading screen after successful authentication
      setShowLoading(true);
    } else {
      setError(true);
      setCode("");
    }
  };

  // Show nothing while checking auth
  if (isCheckingAuth) {
    return (
      <div className="fixed inset-0 bg-[#030303]" />
    );
  }

  // Show access gate if not authorized
  if (!isAuthorized) {
    return (
      <div className="fixed inset-0 bg-[#0D1B2A] flex items-center justify-center z-[100]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md px-6"
        >
          <div className="glass-panel rounded-2xl p-8 border border-white/[0.08] backdrop-blur-xl">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img src="/logo-jd.png" alt="JD" className="h-20 w-auto" />
            </div>

            <h1 className="text-2xl font-light text-center text-[#FAFAF8] mb-2">
              Accès restreint
            </h1>
            <p className="text-sm text-[#415A77] text-center mb-8">
              Veuillez entrer le code d'accès pour continuer.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setError(false);
                  }}
                  placeholder="Code d'accès"
                  autoFocus
                  data-testid="access-code-input"
                  className={`w-full px-4 py-3 text-center text-lg tracking-widest rounded-xl bg-white/[0.03] border ${
                    error 
                      ? "border-red-500/50 focus:border-red-500" 
                      : "border-white/[0.08] focus:border-[#B07D62]/50"
                  } text-[#FAFAF8] placeholder:text-[#415A77]/50 outline-none transition-colors`}
                />
                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-400 text-sm text-center mt-2"
                  >
                    Code incorrect
                  </motion.p>
                )}
              </div>

              <button
                type="submit"
                data-testid="access-submit-btn"
                className="w-full py-3 rounded-xl bg-[#B07D62]/10 border border-[#B07D62]/30 text-[#B07D62] font-medium hover:bg-[#B07D62]/20 hover:border-[#B07D62]/50 transition-all duration-300"
              >
                Entrer
              </button>
            </form>
          </div>

          <p className="text-xs text-[#415A77]/50 text-center mt-6">
            © {new Date().getFullYear()} Julien <span className="text-[#B07D62] italic">D'Amore</span>. Tous droits réservés.
          </p>
        </motion.div>
      </div>
    );
  }

  // Show loading screen after authentication
  if (showLoading) {
    return <LoadingScreen onLoadingComplete={handleLoadingComplete} />;
  }

  // Show main content after loading complete
  if (loadingComplete) {
    return children;
  }

  return null;
}
